import http from '../../../Utils/http';
import responseMessageHandler from '../../../Utils/ResponseMessageHandler';
import moment from 'moment';

const formatDateString = (date) => {
  return date ? moment(date).format('YYYY-MM-DD') : null;
};

const mapZoneLayoutTypeEnum = (zone) => {
  const raw = zone?.layout_type ?? zone?.layoutType ?? null;

  const upper = raw.trim().toUpperCase();
  if (upper === 'FULL_SCREEN_PORTRAIT') return 'FULL_SCREEN_PORTRAIT';
  if (upper === 'FULL_SCREEN_LANDSCAPE') return 'FULL_SCREEN_LANDSCAPE';
  if (upper === 'LANDSCAPE') return 'LANDSCAPE';
  if (upper === 'PORTRAIT') return 'PORTRAIT';
  if (upper === 'LED') return 'LED';
  if (upper === 'CUSTOM') return 'CUSTOM';

  const looksFullScreen =
    zone?.isFullScreen ||
    zone?.zone_type === 'fullscreen' ||
    (typeof raw === 'string' &&
      ['FULL_SCREEN', 'FULL SCREEN', 'FULLSCREEN'].includes(raw.trim().toUpperCase()));

  if (looksFullScreen) {
    const orientation = (zone?.orientation || '').toString().toLowerCase();
    if (orientation === 'portrait') return 'FULL_SCREEN_PORTRAIT';
    if (orientation === 'landscape') return 'FULL_SCREEN_LANDSCAPE';

    const res = zone?.layoutResolution || {};
    const w = Number(res.width || zone?.width);
    const h = Number(res.height || zone?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return h > w ? 'FULL_SCREEN_PORTRAIT' : 'FULL_SCREEN_LANDSCAPE';
    }
    return 'FULL_SCREEN_LANDSCAPE';
  }

  if (String(zone?.layoutType || '').toUpperCase() === 'LED' || String(zone?.zone_type || '').toLowerCase() === 'led') {
    return 'LED';
  }

  const res = zone?.layoutResolution || {};
  const w = Number(res.width || zone?.width);
  const h = Number(res.height || zone?.height);
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    if (h > w) return 'PORTRAIT';
    return 'LANDSCAPE';
  }

  return 'CUSTOM';
};

const mapLayoutTypeEnumToName = (enumValue) => {
  if (enumValue === 'LANDSCAPE' || enumValue === 'FULL_SCREEN_LANDSCAPE') return 'Landscape';
  if (enumValue === 'PORTRAIT' || enumValue === 'FULL_SCREEN_PORTRAIT') return 'Portrait';
  if (enumValue === 'LED') return 'LED';
  return 'Custom';
};

const formatZones = (zones, removeId = false) => {
  return zones?.map(zone => {
    const { id, ...zoneWithoutId } = zone;
    const baseZone = removeId ? zoneWithoutId : zone;

    const layoutTypeEnum = mapZoneLayoutTypeEnum(zone);
    const layoutTypeName = mapLayoutTypeEnumToName(layoutTypeEnum);

    return {
      ...baseZone,
      media_type: zone?.media_type === 'Still / Video' ? 'Mixed' : zone?.media_type,
      isFullScreen: !!zone.isFullScreen,
      zone_type: zone?.zone_type || null,
      layoutType: layoutTypeName,
      layout_type: layoutTypeEnum,
      orientation: zone?.orientation || null,
      layoutResolution: zone?.layoutResolution || null,
      priceSchedules: {
        ...zone?.priceSchedules,
        dateRange: {
          ...zone?.priceSchedules?.dateRange,
          startDate: formatDateString(zone?.priceSchedules?.dateRange?.startDate),
          endDate: formatDateString(zone?.priceSchedules?.dateRange?.endDate),
        },
        specifiedDates: {
          ...zone?.priceSchedules?.specifiedDates,
          datesData: zone?.priceSchedules?.specifiedDates?.datesData?.map(dateEntry => ({
            ...dateEntry,
            date: formatDateString(dateEntry.date)
          })) || [],
        }
      }
    };
  });
};

const CreateRateSchedule = {
  name: 'createRateSchedule',
  state: {
    data: {},
    loading: false,
    error: '',
  },
  reducers: {
    createRequested(state) {
      return {
        ...state,
        loading: true,
        error: '',
      };
    },

    createSucceeded(state, payload) {
      return {
        ...state,
        data: payload,
        loading: false,
        error: '',
      };
    },

    createFailed(state, payload) {
      return {
        ...state,
        error: payload,
        loading: false,
      };
    },
  },
  effects: (dispatch) => ({
    async createNewRateSchedule({
      name,
      totalPlaylistTime,
      adTimeAllocation,
      noOfZones,
      zones,
      displayIds,
      zonesPreviewSnapshot,
      zonesPreviewSnapshots,
      advertiserIds,
      specializedRates,
      default_id,
      layout_width,
      layout_height,
      layout_ids,
    }) {
      try {
        this.createRequested();
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const org_id = userInfo.org_id;
        const formattedZones = formatZones(zones);
        let finalLayoutWidth = layout_width ?? null;
        let finalLayoutHeight = layout_height ?? null;

        if (finalLayoutWidth == null || finalLayoutHeight == null) {
          const hasCustomLayout = Array.isArray(zones)
            ? zones.some((z) => mapZoneLayoutTypeEnum(z) === 'CUSTOM')
            : false;
          if (hasCustomLayout) {
            const primaryZone = Array.isArray(zones) && zones.length > 0 ? zones[0] : null;
            const primaryResolution = primaryZone?.layoutResolution || {};
            finalLayoutWidth = primaryResolution?.width || null;
            finalLayoutHeight = primaryResolution?.height || null;
          }
        }
        const _default = {
          name,
          totalPlaylistTime,
          adTimeAllocation,
          noOfZones,
          zones: formattedZones,
          layout_width: finalLayoutWidth,
          layout_height: finalLayoutHeight,
          layout_ids: Array.isArray(layout_ids) ? layout_ids : [],
          org_id,
          displayIds,
          zonesPreviewSnapshot,
          zonesPreviewSnapshots: Array.isArray(zonesPreviewSnapshots)
            ? zonesPreviewSnapshots
            : [],
          user_id: userInfo.id,
          advertiserIds,
          default_id: default_id || null,
        };
        const _specialized = [];
        if (specializedRates?.length > 0) {
          await Promise.all(specializedRates.map(async (_rate) => {
            const specializedZones = formatZones(_rate.zones);
            const specPrimary = Array.isArray(_rate.zones) && _rate.zones.length > 0 ? _rate.zones[0] : null;
            const specResolution = specPrimary?.layoutResolution || {};
            const specHasCustom = Array.isArray(_rate.zones)
              ? _rate.zones.some((z) => mapZoneLayoutTypeEnum(z) === 'CUSTOM')
              : false;
            let specLayoutWidth = _rate.layout_width ?? null;
            let specLayoutHeight = _rate.layout_height ?? null;
            if ((specLayoutWidth == null || specLayoutHeight == null) && specHasCustom) {
              specLayoutWidth = specResolution?.width || null;
              specLayoutHeight = specResolution?.height || null;
            }
            _specialized.push({
              name: _rate.name,
              totalPlaylistTime: _default.totalPlaylistTime,
              adTimeAllocation: _default.adTimeAllocation,
              noOfZones: _default.noOfZones,
              zonesPreviewSnapshot: _default.zonesPreviewSnapshot,
              zonesPreviewSnapshots: _default.zonesPreviewSnapshots,
              zones: specializedZones,
              advertiserIds: _rate.advertiserIds,
              description: _rate.description || '',
              layout_width: specLayoutWidth,
              layout_height: specLayoutHeight,
              layout_ids: Array.isArray(_rate.layout_ids) ? _rate.layout_ids : _default.layout_ids,
            });
            if (default_id) {
              _specialized.default_id = default_id;
            }
          }));
        }

        const payload = {
          default: _default,
          specialized: _specialized,
          user_id: userInfo.id,
          org_id,
        };

        const response = await http.post('savvy/rate-schedule', payload);
        if (response && response.data) {
          dispatch.ProgressStepDSO.UpdateGetDSOProgressStep(2);
        }
        this.createSucceeded(response.data);

        dispatch({
          type: 'helper/show',
          payload: {
            type: 'success',
            message: 'Rate schedule created successfully!',
            show: true,
          },
        });
      } catch (error) {
        this.createFailed(error.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(error.message),
            show: true,
          },
        });
      }
    },

    async addSpecializedRateToExisting({
      default_id,
      name,
      description,
      zones,
      totalPlaylistTime,
      adTimeAllocation,
      zonesPreviewSnapshot,
      advertiserIds,
    }) {
      try {
        this.createRequested();
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const org_id = userInfo.org_id;
        const formattedZones = formatZones(zones, true); // Remove id from zones for specialized rates

        const specializedRate = {
          name,
          description: description || '',
          zones: formattedZones,
          noOfZones: formattedZones.length,
          totalPlaylistTime,
          adTimeAllocation,
          zonesPreviewSnapshot,
          advertiserIds,
        };

        const payload = {
          default: { id: default_id },
          specialized: [specializedRate],
          user_id: userInfo.id,
          org_id,
        };


        const response = await http.post('savvy/rate-schedule', payload);
        this.createSucceeded(response.data);

        dispatch({
          type: 'helper/show',
          payload: {
            type: 'success',
            message: 'Specialized rate added successfully!',
            show: true,
          },
        });

        return response.data;
      } catch (error) {
        this.createFailed(error.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(error.message),
            show: true,
          },
        });
        throw error;
      }
    },

    async editSpecializedRate({
      specializedRateId,
      default_id,
      name,
      description,
      zones,
      advertiserIds,
    }) {
      try {
        this.createRequested();
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const org_id = userInfo.org_id;
        const formattedZones = formatZones(zones, false); // Remove id from zones for specialized rates

        const payload = {
          id: specializedRateId,
          default_id,
          name,
          description: description || '',
          zones: formattedZones,
          advertiserIds,
          user_id: userInfo.id,
          org_id,
        };


        const Id = specializedRateId;
        const response = await http.put(`savvy/rate-schedule/${Id}`, payload);
        this.createSucceeded(response.data);

        dispatch({
          type: 'helper/show',
          payload: {
            type: 'success',
            message: 'Specialized rate updated successfully!',
            show: true,
          },
        });

        return response.data;
      } catch (error) {
        this.createFailed(error.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(error.message),
            show: true,
          },
        });
        throw error;
      }
    },

    async removeSpecializedRate({ specializedRateId }) {
      try {
        this.createRequested();
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const org_id = userInfo.org_id;
        const user_id = userInfo.id;

        const url = `savvy/rate-schedule/${specializedRateId}?user_id=${user_id}&org_id=${org_id}`;
        const response = await http.delete(url);

        this.createSucceeded(response.data);

        dispatch({
          type: 'helper/show',
          payload: {
            type: 'success',
            message: 'Specialized rate removed successfully!',
            show: true,
          },
        });

        return response.data;
      } catch (error) {
        this.createFailed(error.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(error.message),
            show: true,
          },
        });
        throw error;
      }
    },
  }),
};

export default CreateRateSchedule;

